import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { TextField, Typography, Tab, Switch, Box, Grid, Backdrop, Card } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import {
  getRolesAction,
  getTerminalsAction,
} from "src/app-redux/management/actions/managementActions";
import {
  loading,
  selectCurrentUser,
  selectTerminalListing,
} from "src/app-redux/management/managementSlice";
import { INewUser } from "src/common/typings/user";
import UseWindowDimensions from "src/common/utils/getWindowDimensions";
import CommonTabs from "src/components/CommonTabs/CommonTabs";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { allTerminalsList } from "src/common/utils/app.dropdowns";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CommonLoader from "src/components/Loader/CommonLoader";

function a11yProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(17),
    flexBasis: "33.33%",
    flexShrink: 0,
    lineHeight: "28px",
  },
  tabPanelHead: {
    display: "flex",
    width: "100%",
    margin: "0px 0 0px",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "Inter",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
  },
}));

export interface IForm extends INewUser {}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .email("Please enter a valid email"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(50, "Maximum 50 characters allowed"),
  username: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required"),
  terminalId: Yup.string().required("This field is required"),
  firstName: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .matches(/^[-\sa-zA-Z]+$/, "Only alphabets are allowed for this field "),
  lastName: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .matches(/^[-\sa-zA-Z]+$/, "Only alphabets are allowed for this field "),
  roles: Yup.array<string[]>().min(1, "At least one role is required."),
  phoneNumber: Yup.string()
    .max(20, "Maximum 12 characters allowed")
    .min(6, "min 12 characters allowed")
    .test("len", "Invalid Phone Number No.", (val = "") => {
      const val_length_without_dashes = val.replace(/-|_/g, "").length;
      return val_length_without_dashes === 13;
    })
    .required("This field is required"),
  appointment: Yup.string().max(30, "Maximum 30 characters allowed"),
});
interface Props {
  allRoles: string[];
  submitCounter: number;
  handleSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IForm;
  selectedUserRoles: any;
  setDisable: (val: boolean) => void;
}

export function AddUpdateUser({
  initialValues,
  handleSubmit,
  submitCounter,
  allRoles,
  setDisable,
}: Props): React.ReactElement {
  const classes: any = useStyles();
  const dispatch = useAppDispatch();
  const [value, setValue] = React.useState(0);
  const [terminalId, setTerminalId] = React.useState<number>();
  const multiselectRef: any = useRef();
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };
  const currentUser = useAppSelector(selectCurrentUser);
  const getAllTerminal = useAppSelector(selectTerminalListing);
  const loadingManagement = useAppSelector(loading);
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      isADUser: true,
      email: initialValues?.email || "",
      isActive: initialValues?.isActive ? Boolean(initialValues.isActive) : false,
      username: initialValues?.username || "",
      firstName: initialValues?.firstName || "",
      lastName: initialValues?.lastName || "",
      phoneNumber: initialValues?.phoneNumber ? `${initialValues.phoneNumber}` : "",
      twoFAEnabled: initialValues?.twoFAEnabled || false,
      location: initialValues?.location || "",
      terminalId: initialValues?.terminalId || "",
      appointment: initialValues?.appointment || "",
      enabledAppNotificiations: initialValues?.enabledAppNotificiations || false,
      enabledSMSNotifications: initialValues?.enabledSMSNotifications || false,
      isAllowToExportData: initialValues?.isAllowToExportData
        ? Boolean(initialValues.isAllowToExportData)
        : false,
      roles: initialValues?.roles ? initialValues.roles : [],
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      const invalidRole = initialValues?.roles?.find(
        (item: any) => item?.roleName == "INVALID PERMISSION"
      );
      if (invalidRole) {
        const roles = [...values.roles, invalidRole.id];
        const body = { ...values, roles: roles };
        handleSubmit(body);
        return;
      }
      handleSubmit(values);
    },
  });
  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);

  useEffect(() => {
    if (currentUser) {
      setTerminalId(Number(currentUser?.terminalId));
    }
  }, [currentUser]);

  const { height } = UseWindowDimensions();
  const minAccHeight = 194;
  const minTabHeight = 214;
  let accordionMaxHeight = 0;
  let tabMinHeight = 0;
  accordionMaxHeight = height - minAccHeight;
  tabMinHeight = height - minTabHeight;
  useEffect(() => {
    dispatch(getRolesAction());
    dispatch(getTerminalsAction());
  }, []);

  const [selctedItems, setSelctedItems] = React.useState<any>([]);
  React.useEffect(() => {
    if (currentUser?.roles) {
      const arr = currentUser?.roles;
      setSelctedItems(arr);
      formik.setFieldValue(
        "roles",
        arr.map((item: any) => (item.id ? item.id : ""))
      );
    }
  }, [currentUser]);
  const onMultiSelectSelection = (selectedList: any) => {
    formik.setFieldValue(
      "roles",
      selectedList.map((item: any) => (item.id ? item.id : ""))
    );
    setDisable(false);
  };
  const onMultiSelectRemove = (selectedList: any) => {
    formik.setFieldValue(
      "roles",
      selectedList.map((item: any) => (item.id ? item.id : ""))
    );
    setDisable(false);
  };
  const handleValueChange = (event: any) => {
    formik.handleChange(event);
    setDisable(false);
  };
  function handleValueChangeTerminal(terId: number) {
    setTerminalId(terId);
    const current = multiselectRef?.current;
    if (current) {
      current?.getUpdated();
    }
    formik.setFieldValue("roles", []);
  }
  const [state, setState] = useState({ showPassword: false });

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={loadingManagement}>
        <CommonLoader />
      </Backdrop>
      <Grid container direction="row" alignItems="flex-start">
        <Grid item xs={12} sm={4} md={2}>
          <CommonTabs value={value} handleChange={handleChange} tabMinHeight={tabMinHeight}>
            <Tab label="User information" {...a11yProps(0)} />
          </CommonTabs>
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          md={10}
          component={Card}
          className={classes.tabCardContent}
          style={{ height: accordionMaxHeight }}
        >
          <Grid className={classes.UserMainPanel}>
            <form onSubmit={formik.handleSubmit}>
              <Grid className={classes.formRow}>
                <Grid className={classes.tabPanelHead}>
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.heading}>Personal Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {" "}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.isActive)}
                          onChange={handleValueChange}
                          name="isActive"
                          color="primary"
                        />
                      }
                      label="Active User"
                      style={{ float: "right" }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box
                  mb={2}
                  borderBottom={1}
                  style={{ marginTop: "-3px" }}
                  color={"rgba(0, 0, 0, 0.12)"}
                />
              </Grid>
              <Grid container direction="row" alignItems="center" spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="First Name"
                      name="firstName"
                      placeholder="Michael"
                      value={formik.values.firstName}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                      helperText={formik.touched.firstName && formik.errors.firstName}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Last Name"
                      name="lastName"
                      placeholder="Jack"
                      value={formik.values.lastName}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                      helperText={formik.touched.lastName && formik.errors.lastName}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      disabled={Boolean(initialValues?.email)}
                      required
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      placeholder="Jack"
                      label="User Name"
                      name="username"
                      className="customTextField"
                      value={formik.values.username}
                      onChange={handleValueChange}
                      error={formik.touched.username && Boolean(formik.errors.username)}
                      helperText={formik.touched.username && formik.errors.username}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Email"
                      name="email"
                      placeholder="hello@there.you"
                      type="email"
                      value={formik.values.email}
                      onChange={handleValueChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <InputMask
                      mask="+99-999-999-9999"
                      value={formik.values.phoneNumber}
                      disabled={false}
                      onChange={handleValueChange}
                    >
                      {() => (
                        <TextField
                          required
                          size="small"
                          fullWidth
                          className="customTextField"
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Phone Number"
                          name="phoneNumber"
                          placeholder="+92-123-456-7890"
                          type="text"
                          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                        />
                      )}
                    </InputMask>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Password"
                      className="customTextField"
                      name="password"
                      placeholder="Enter Password"
                      type={state.showPassword ? "text" : "password"}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {state.showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      error={formik.touched.location && Boolean(formik.errors.location)}
                      helperText={formik.touched.location && formik.errors.location}
                      label="Location"
                      placeholder="Select Location"
                      name="location"
                      optionsType={allTerminalsList}
                      getOptionLabel={(option: any) => option.name || ""}
                      value={
                        allTerminalsList.find((st: any) => st.name === formik.values.location) ||
                        null
                      }
                      onChange={(option: any) => {
                        if (option && option.id !== 0) {
                          formik.setFieldValue("location", option.name);
                          handleValueChange({ target: { value: option.name } });
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      size="small"
                      className="customTextField"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Appointment"
                      name="appointment"
                      placeholder=""
                      value={formik.values.appointment}
                      disabled={false}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.appointment && Boolean(formik.errors.appointment)}
                      helperText={formik.touched.appointment && formik.errors.appointment}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <CommonAutoCompleteField
                      required
                      cssClassName="customTextField"
                      error={formik.touched.terminalId && Boolean(formik.errors.terminalId)}
                      helperText={formik.touched.terminalId && formik.errors.terminalId}
                      label="Terminal"
                      placeholder="Select Terminal"
                      name="terminalId"
                      optionsType={getAllTerminal.filter((data: any) => {
                        if (Number(localStorage.getItem("terminalId")) !== 0) {
                          return (
                            data.isActive &&
                            data.terminalId == Number(localStorage.getItem("terminalId"))
                          );
                        } else {
                          return data.isActive && data.terminalId != 0;
                        }
                      })}
                      getOptionLabel={(option: any) => option.terminalName || ""}
                      value={
                        getAllTerminal.find(
                          (st: any) => st.terminalId === formik.values.terminalId
                        ) || null
                      }
                      onChange={(option: any) => {
                        formik.setFieldValue("terminalId", option.terminalId);
                        handleValueChange({ target: { value: option.terminalId } });
                        handleValueChangeTerminal(option.terminalId);
                      }}
                      disabled={Number(localStorage.getItem("terminalId")) != 0}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiSelect2
                    allValues={
                      Number(localStorage.getItem("terminalId")) !== 0
                        ? allRoles?.filter((item: any) => item?.roleName != "INVALID PERMISSION")
                        : allRoles?.filter(
                            (item: any) =>
                              item?.roleName != "INVALID PERMISSION" &&
                              item?.terminalId == terminalId
                          )
                    }
                    selctedItems={selctedItems?.filter(
                      (item: any) => item?.roleName != "INVALID PERMISSION"
                    )}
                    displayValue={"roleName"}
                    placeholder="User Roles"
                    onSelect={onMultiSelectSelection}
                    onRemove={onMultiSelectRemove}
                    errorText={formik.touched.roles && formik.errors.roles}
                    ref={multiselectRef}
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={3} xl={2}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.enabledAppNotificiations}
                          onChange={handleValueChange}
                          name="enabledAppNotificiations"
                          color="primary"
                        />
                      }
                      label="In-App notifications"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.enabledSMSNotifications}
                          onChange={handleValueChange}
                          name="enabledSMSNotifications"
                          color="primary"
                        />
                      }
                      label="SMS notifications"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.isAllowToExportData)}
                          onChange={handleValueChange}
                          name="isAllowToExportData"
                          color="primary"
                        />
                      }
                      label="Allow to Export Data"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.twoFAEnabled}
                          onChange={handleValueChange}
                          name="twoFAEnabled"
                          color="primary"
                        />
                      }
                      label="Enable 2FA"
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
